import React, { FC, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import {
  addUtilityThreshold,
  deleteUtilityThreshold,
} from "api";
import { Label12SemiBold, MultiSelectControlledOption, Tooltip } from "components";
import { useNotify } from "hooks";
import { Controller, useForm } from "react-hook-form";
import { EsgDeleteIcon } from "assets";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";
import { CustomSmallOutlinedInput, tableStyle } from "./styles";
import { ThresholdValueModalProps } from "../../types";

export const ThresholdValueModal: FC<ThresholdValueModalProps> = ({
  isOpen,
  onClose,
  allowedValues,
  showException,
  utilityThresholdData,
}) => {
  // ---- Hook ----
  const notify = useNotify();

  // ---- State ----
  const [selectedYoYInputValue, setSelectedYoYInputValue] = useState<string>("");
  const [selectedMoMInputValue, setSelectedMoMInputValue] = useState<string>("");
  const [selectedUtilityTypes, setSelectedUtilityTypes] = useState<MultiSelectControlledOption[]>([]);
  const [selectedSites, setSelectedSites] = useState<MultiSelectControlledOption[]>([]);

  const allUtilityTypes = useMemo(() =>
    allowedValues
      ? allowedValues.utilityTypes
        .sort((utA: any, utB: any) => (utA.name > utB.name ? 1 : -1))
        .map((ut: any) => ({ name: ut.name, label: ut.name, value: ut.id }))
      : [],
    [allowedValues],
  );

  const allSites = useMemo(() =>
    allowedValues
      ? allowedValues.sites.map((site: any) => ({
        name: site.siteName,
        label: site.siteName,
        value: site.siteId,
      }))
      : [],
    [allowedValues],
  );

  const { control } = useForm<any>({
    mode: "onChange",
  });

  // ---- API ----
  const { mutateAsync: addUtilityThresholds } = useMutation(addUtilityThreshold);
  const { mutateAsync: deleteUtilityThresholds } = useMutation(deleteUtilityThreshold);

  // ---- Page Function ----
  const handleClose = () => {
    onClose();
  };

  const handleMoMChange = (e: any) => {
    const { target } = e;
    const inputVal = target;
    setSelectedMoMInputValue(inputVal.value);
  };

  const handleYoYChange = (e: any) => {
    const { target } = e;
    const inputVal = target;
    setSelectedYoYInputValue(inputVal.value);
  };

  const handleSaveChange = () => {
    const payloadUtilityTypeValues = selectedUtilityTypes.map((ut) => ut.name);
    const payloadSitesValues = selectedSites.map((site) => site.value as number);
    const payloadMomValue = parseInt(selectedMoMInputValue, 10);
    const payloadYoyValue = parseInt(selectedYoYInputValue, 10);

    const payload = {
      utilityTypes: selectedUtilityTypes.length === allUtilityTypes.length || selectedUtilityTypes.length === 0
        ? null : payloadUtilityTypeValues,
      siteIds: selectedSites.length === allSites.length || selectedSites.length === 0
        ? null : payloadSitesValues,
      sequentialThreshold: payloadMomValue,
      yearlyThreshold: payloadYoyValue,
    }

    if (selectedMoMInputValue === "" || selectedYoYInputValue === "") {
      notify.error("Please complete all the fields!");
    }
    else if (Number.isNaN(Number(payloadMomValue)) || Number.isNaN(Number(payloadYoyValue))) {
      notify.error("Please enter only numbers into the MoM and YoY fields!");
    } else {
      addUtilityThresholds(payload, {
        onSuccess: () => {
          notify.success("Threshold values added!")
          handleClose();
        },
        onError: () => {
          notify.error("Some error has happen while adding Threshold values!")
        }
      })
    }
  }

  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "id",
      header: '',
      Cell: ({ row }) => {
        const rowId = row?.original?.id;
        return (
          <Box
            sx={{
              display: "flex",
              borderRight: '1px solid rgba(224,224,224,1)', height: '45px', alignItems: 'center', paddingRight: '5px'
            }}
          >
            <Tooltip arrow placement="top" title="De-activated">
              <IconButton
                sx={{ width: "35px", height: "35px" }}
                onClick={() => {
                  deleteUtilityThresholds(rowId, {
                    onSuccess: () => {
                      handleClose();
                      notify.success("Threshold values deleted!")
                    },
                    onError: () => {
                      notify.error("Some error has happen while deleting Threshold values! ")
                    }
                  })
                }}
              >
                <EsgDeleteIcon color="red" />
              </IconButton>
            </Tooltip>
          </Box>
        )
      },
      size: 60,
    },
    {
      accessorKey: "utilityType",
      header: "Utility Type",
      size: 120,
      Cell: ({ cell }: any) => {
        const value: any = cell.getValue();
        if (!value) {
          return "All Utility Type";
        }
        return (
          <Tooltip title={value! as string}>
            <Box sx={{ textAlign: "center", cursor: "pointer" }}>{value}</Box>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "siteName",
      header: "Site",
      size: 120,
      Cell: ({ cell }: any) => {
        const value: any = cell.getValue();
        if (!value) {
          return "All Sites";
        }
        return (
          <Tooltip title={value! as string}>
            <Box sx={{ textAlign: "center", cursor: "pointer" }}>{value}</Box>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "sequentialThreshold",
      header: "MoM",
      size: 100,
    },
    {
      accessorKey: "yearlyThreshold",
      header: "YoY",
      size: 100,
    },
  ];

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {" "}
        {showException ? "Exceptions" : "Add an Exception"}
      </DialogTitle>
      <DialogContent>
        {!showException ? <>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, width: "500px" }}>
            {allUtilityTypes && <EsgMultiselectControlledField
              label="Utility Types"
              selectedValues={selectedUtilityTypes}
              setSelectedValues={setSelectedUtilityTypes}
              options={allUtilityTypes}
              type="rounded"
            />}
            {allSites && <EsgMultiselectControlledField
              label="Sites"
              selectedValues={selectedSites}
              setSelectedValues={setSelectedSites}
              options={allSites}
              type="rounded"
            />}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
            <Box>
              <Label12SemiBold>MoM</Label12SemiBold>
              <Controller
                name="MoM"
                control={control}
                render={() => (
                  <CustomSmallOutlinedInput
                    endAdornment="%"
                    onChange={handleMoMChange}
                  />
                )}
              />
            </Box>
            <Box>
              <Label12SemiBold>YoY</Label12SemiBold>
              <Controller
                name="YoY"
                control={control}
                render={() => (
                  <CustomSmallOutlinedInput
                    endAdornment="%"
                    onChange={handleYoYChange}
                  />
                )}
              />
            </Box>
          </Box>
        </> : (
          <MaterialReactTable
            columns={columns}
            muiTableContainerProps={{
              sx: tableStyle,
            }}
            muiTablePaperProps={{
              sx: {
                borderRadius: "10px",
                boxShadow: "none",
              },
            }}
            muiTopToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                lineHeight: "30px",
                "& .MuiBadge-root": {
                  display: "none",
                },
              },
            }}
            muiTableHeadRowProps={{
              sx: {
                "& > th": {
                  paddingTop: 1,
                  paddingBottom: 1,
                },
                height: "auto",
                backgroundColor: "#F8FAFC",
              },
            }}
            muiTableBodyRowProps={{
              sx: {
                height: "45px",
                backgroundColor: "white",
              },
            }}
            data={utilityThresholdData}
            enableColumnResizing
            enableDensityToggle={false}
            initialState={{ density: "compact" }}
            enablePagination={false}
            enableRowVirtualization
          />
        )}
      </DialogContent>
      {!showException && <DialogActions>
        <Grid
          container
          alignItems="center"
          justifyContent="flex-end"
          mb={2}
          ml={2}
          px={2}
        >
          <Grid item>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={() => handleSaveChange()}
              variant="contained"
              color="primary"
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </DialogActions>}
    </Dialog>
  );
};
